'use client';
import React, { useState } from 'react';
import { Search, ChevronDown, RefreshCw, AlertCircle, Package, FileText } from 'lucide-react';
import BLResultPanel from './BLResultPanel';
import AlertSubscribeModal from './AlertSubscribeModal';

const CARRIERS = [
  'All Carriers',
  'Maersk',
  'MSC',
  'CMA CGM',
  'Hapag-Lloyd',
  'ONE Line',
  'Evergreen',
  'COSCO',
  'Yang Ming',
  'HMM',
  'ZIM',
  'OOCL',
  'PIL',
  'SCI India',
  'Shreyas Shipping',
];

// Mock BL data — backend integration point: replace with carrier API call
const MOCK_BL_DATA: Record<string, object> = {
  'MAEU123456789': {
    blNumber: 'MAEU123456789',
    carrier: 'Maersk',
    carrierCode: 'MAEU',
    vessel: 'Maersk Emerald',
    voyage: 'AE-1 / 412E',
    origin: { port: 'Nhava Sheva (JNPT)', code: 'INNSA', country: 'India' },
    destination: { port: 'Rotterdam', code: 'NLRTM', country: 'Netherlands' },
    status: 'In Transit',
    eta: '18/09/2026',
    etd: '04/09/2026',
    lastUpdate: '06/09/2026 14:32 IST',
    containers: [
      { id: 'cont-MSKU1234567', number: 'MSKU1234567', type: '40HC', weight: '18.4 MT', status: 'In Transit', seal: 'SL-9823441' },
      { id: 'cont-MSKU7654321', number: 'MSKU7654321', type: '20GP', weight: '11.2 MT', status: 'In Transit', seal: 'SL-9823442' },
    ],
    events: [
      { id: 'ev-1', date: '06/09/2026', time: '14:32', location: 'Arabian Sea', event: 'Vessel In Transit', status: 'In Transit', current: true },
      { id: 'ev-2', date: '04/09/2026', time: '22:15', location: 'Nhava Sheva (JNPT), India', event: 'Vessel Departed', status: 'Vessel Departed', current: false },
      { id: 'ev-3', date: '03/09/2026', time: '09:40', location: 'Nhava Sheva (JNPT), India', event: 'Cargo Loaded on Vessel', status: 'Cargo Received', current: false },
      { id: 'ev-4', date: '01/09/2026', time: '16:20', location: 'Nhava Sheva (JNPT), India', event: 'Cargo Received at Terminal', status: 'Cargo Received', current: false },
      { id: 'ev-5', date: '28/08/2026', time: '11:00', location: 'Mumbai, India', event: 'Booking Confirmed', status: 'Booking Confirmed', current: false },
    ],
  },
  'MSCU987654321': {
    blNumber: 'MSCU987654321',
    carrier: 'MSC',
    carrierCode: 'MSCU',
    vessel: 'MSC Ambra',
    voyage: 'AE-7 / 628W',
    origin: { port: 'Mundra Port', code: 'INMUN', country: 'India' },
    destination: { port: 'Felixstowe', code: 'GBFXT', country: 'United Kingdom' },
    status: 'Customs Hold',
    eta: '22/09/2026',
    etd: '02/09/2026',
    lastUpdate: '05/09/2026 09:18 IST',
    containers: [
      { id: 'cont-MSCU3456789', number: 'MSCU3456789', type: '40GP', weight: '22.1 MT', status: 'Customs Hold', seal: 'SL-7712330' },
    ],
    events: [
      { id: 'ev-6', date: '05/09/2026', time: '09:18', location: 'Suez Canal, Egypt', event: 'Customs Hold — Documentation Review', status: 'Customs Hold', current: true },
      { id: 'ev-7', date: '03/09/2026', time: '04:50', location: 'Red Sea', event: 'Vessel In Transit', status: 'In Transit', current: false },
      { id: 'ev-8', date: '02/09/2026', time: '18:30', location: 'Mundra Port, India', event: 'Vessel Departed', status: 'Vessel Departed', current: false },
      { id: 'ev-9', date: '31/08/2026', time: '12:00', location: 'Mundra Port, India', event: 'Cargo Loaded on Vessel', status: 'Cargo Received', current: false },
    ],
  },
};

// Mock Container data — backend integration point: replace with carrier API call
const MOCK_CONTAINER_DATA: Record<string, object> = {
  'MSKU1234567': {
    blNumber: 'MAEU123456789',
    carrier: 'Maersk',
    carrierCode: 'MAEU',
    vessel: 'Maersk Emerald',
    voyage: 'AE-1 / 412E',
    origin: { port: 'Nhava Sheva (JNPT)', code: 'INNSA', country: 'India' },
    destination: { port: 'Rotterdam', code: 'NLRTM', country: 'Netherlands' },
    status: 'In Transit',
    eta: '18/09/2026',
    etd: '04/09/2026',
    lastUpdate: '06/09/2026 14:32 IST',
    containers: [
      { id: 'cont-MSKU1234567', number: 'MSKU1234567', type: '40HC', weight: '18.4 MT', status: 'In Transit', seal: 'SL-9823441' },
    ],
    events: [
      { id: 'ev-c1', date: '06/09/2026', time: '14:32', location: 'Arabian Sea', event: 'Container In Transit on Vessel', status: 'In Transit', current: true },
      { id: 'ev-c2', date: '04/09/2026', time: '22:15', location: 'Nhava Sheva (JNPT), India', event: 'Container Loaded — Vessel Departed', status: 'Vessel Departed', current: false },
      { id: 'ev-c3', date: '03/09/2026', time: '09:40', location: 'Nhava Sheva (JNPT), India', event: 'Container Loaded on Vessel', status: 'Cargo Received', current: false },
      { id: 'ev-c4', date: '01/09/2026', time: '16:20', location: 'Nhava Sheva (JNPT), India', event: 'Container Received at Terminal', status: 'Cargo Received', current: false },
    ],
  },
  'MSCU3456789': {
    blNumber: 'MSCU987654321',
    carrier: 'MSC',
    carrierCode: 'MSCU',
    vessel: 'MSC Ambra',
    voyage: 'AE-7 / 628W',
    origin: { port: 'Mundra Port', code: 'INMUN', country: 'India' },
    destination: { port: 'Felixstowe', code: 'GBFXT', country: 'United Kingdom' },
    status: 'Customs Hold',
    eta: '22/09/2026',
    etd: '02/09/2026',
    lastUpdate: '05/09/2026 09:18 IST',
    containers: [
      { id: 'cont-MSCU3456789', number: 'MSCU3456789', type: '40GP', weight: '22.1 MT', status: 'Customs Hold', seal: 'SL-7712330' },
    ],
    events: [
      { id: 'ev-cc1', date: '05/09/2026', time: '09:18', location: 'Suez Canal, Egypt', event: 'Customs Hold — Documentation Review', status: 'Customs Hold', current: true },
      { id: 'ev-cc2', date: '03/09/2026', time: '04:50', location: 'Red Sea', event: 'Container In Transit', status: 'In Transit', current: false },
      { id: 'ev-cc3', date: '02/09/2026', time: '18:30', location: 'Mundra Port, India', event: 'Container Loaded — Vessel Departed', status: 'Vessel Departed', current: false },
    ],
  },
};

type TrackMode = 'bl' | 'container';

export default function HeroSearch() {
  const [trackMode, setTrackMode] = useState<TrackMode>('bl');
  const [query, setQuery] = useState('');
  const [carrier, setCarrier] = useState('All Carriers');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<object | null>(null);
  const [error, setError] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleSearch = () => {
    const trimmed = query.trim().toUpperCase();
    if (!trimmed) {
      setError(`Please enter a ${trackMode === 'bl' ? 'Bill of Lading' : 'Container'} number to search.`);
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    // Backend integration point: replace with API call to carrier tracking service
    setTimeout(() => {
      if (trackMode === 'bl') {
        const found = MOCK_BL_DATA[trimmed];
        if (found) {
          setResult(found);
        } else {
          setError(`No tracking data found for BL "${trimmed}". Try MAEU123456789 or MSCU987654321 for demo.`);
        }
      } else {
        const found = MOCK_CONTAINER_DATA[trimmed];
        if (found) {
          setResult(found);
        } else {
          setError(`No tracking data found for Container "${trimmed}". Try MSKU1234567 or MSCU3456789 for demo.`);
        }
      }
      setLoading(false);
    }, 1400);
  };

  const handleRefresh = () => {
    if (!result) return;
    setRefreshing(true);
    // Backend integration point: re-fetch latest status from carrier API
    setTimeout(() => setRefreshing(false), 1200);
  };

  const handleModeSwitch = (mode: TrackMode) => {
    setTrackMode(mode);
    setQuery('');
    setResult(null);
    setError('');
  };

  return (
    <section>
      {/* Hero gradient */}
      <div className="gradient-ocean rounded-xl px-6 py-10 md:py-14 mb-6 text-center">
        <h1 className="text-hero text-white mb-3">
          Track Your Shipment
        </h1>
        <p className="text-base text-blue-100 max-w-xl mx-auto mb-6">
          Real-time shipment tracking across 26 major carriers. Track by Bill of Lading or Container Number.
        </p>

        {/* Track Mode Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-white/10 border border-white/20 rounded-xl p-1 gap-1 backdrop-blur-sm">
            <button
              onClick={() => handleModeSwitch('bl')}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                trackMode === 'bl' ?'bg-white text-primary shadow-md' :'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <FileText size={15} />
              Track BL
            </button>
            <button
              onClick={() => handleModeSwitch('container')}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                trackMode === 'container' ?'bg-white text-primary shadow-md' :'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <Package size={15} />
              Track Container
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3">
          {/* Carrier dropdown */}
          <div className="relative">
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="appearance-none h-12 pl-4 pr-8 rounded-lg bg-white/10 border border-white/20 text-white text-sm font-medium backdrop-blur-sm focus:outline-none focus:border-white/50 cursor-pointer min-w-[140px]"
            >
              {CARRIERS.map((c) => (
                <option key={`carrier-opt-${c}`} value={c} className="text-foreground bg-card">
                  {c}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none" />
          </div>

          {/* Input */}
          <div className="flex-1 relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={
                trackMode === 'bl' ?'Enter BL Number (e.g. MAEU123456789)' :'Enter Container Number (e.g. MSKU1234567)'
              }
              className="w-full h-12 px-4 rounded-lg bg-white text-foreground text-sm font-medium placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          {/* Search button */}
          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn-primary h-12 px-6 flex items-center gap-2 text-sm shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Search size={16} />
            )}
            {loading ? 'Searching...' : trackMode === 'bl' ? 'Track BL' : 'Track Container'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 max-w-2xl mx-auto flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm text-left">
            <AlertCircle size={16} className="shrink-0 text-yellow-300" />
            {error}
          </div>
        )}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="card-surface p-6 shadow-card">
          <div className="flex flex-col gap-4">
            <div className="skeleton-shimmer h-8 w-64 rounded-lg" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[0,1,2,3].map((i) => (
                <div key={`skel-card-${i}`} className="skeleton-shimmer h-20 rounded-lg" />
              ))}
            </div>
            <div className="skeleton-shimmer h-48 rounded-lg" />
          </div>
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <BLResultPanel
          data={result as any}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          onSetAlert={() => setAlertOpen(true)}
        />
      )}

      {/* Alert modal */}
      <AlertSubscribeModal
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        blNumber={(result as any)?.blNumber || ''}
        carrier={(result as any)?.carrier || ''}
      />
    </section>
  );
}